/**
 * TipTap 리치 텍스트 에디터 래퍼
 * Material-UI 기반 고급 편집 기능
 * Reference: e2mholdings BoardEditor
 */

import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { Highlight } from '@tiptap/extension-highlight';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { common, createLowlight } from 'lowlight';
import { Box, IconButton, Divider, Typography, Tooltip } from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  Code as CodeIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as OrderedListIcon,
  FormatQuote as QuoteIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Title as Heading1Icon,
  Abc as Heading2Icon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  TableChart as TableIcon,
  ChecklistRtl as TaskListIcon,
  Highlight as HighlightIcon,
  Palette as PaletteIcon,
  DataObject as CodeBlockIcon,
  FormatIndentIncrease as IndentIcon,
  FormatIndentDecrease as OutdentIcon,
  ViewColumn as ColumnIcon,
  TableRows as RowIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

// Lowlight 인스턴스 생성 (코드 하이라이팅)
const lowlight = createLowlight(common);

function RichTextEditor({ content, onChange, placeholder = '메모를 작성하세요...' }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        },
        dropcursor: {
          color: '#DBEAFE',
          width: 4
        },
        codeBlock: false, // CodeBlockLowlight로 대체
        gapcursor: true, // 커서 이동 안정화
        history: {
          depth: 100, // Undo/Redo 히스토리
          newGroupDelay: 500 // 타이핑 그룹화 지연
        },
        listItem: {
          HTMLAttributes: {
            class: 'list-item'
          }
        }
      }),
      // 텍스트 정렬
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      // 밑줄
      Underline,
      // 링크
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }),
      // 이미지
      Image.configure({
        HTMLAttributes: {
          class: 'editor-image'
        }
      }),
      // 플레이스홀더
      Placeholder.configure({
        placeholder
      }),
      // 테이블
      Table.configure({
        resizable: true
      }),
      TableRow,
      TableHeader,
      TableCell,
      // 작업 목록
      TaskList,
      TaskItem.configure({
        nested: true
      }),
      // 코드 블록 (하이라이팅 포함)
      CodeBlockLowlight.configure({
        lowlight
      }),
      // 하이라이트
      Highlight.configure({
        multicolor: true
      }),
      // 색상
      TextStyle,
      Color
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    // Tab 키 처리 및 기본 동작 보존
    editorProps: {
      handleKeyDown: (view, event) => {
        // Tab 키만 커스텀 처리
        if (event.key === 'Tab') {
          event.preventDefault();

          const { state } = view;
          const { selection } = state;
          const { $from } = selection;

          if (event.shiftKey) {
            // Shift+Tab: 내어쓰기
            if ($from.parent.type.name === 'listItem') {
              editor.commands.liftListItem('listItem');
              return true;
            }
          } else {
            // Tab: 들여쓰기
            if ($from.parent.type.name === 'listItem') {
              editor.commands.sinkListItem('listItem');
              return true;
            } else {
              // 일반 텍스트에서는 4칸 공백 삽입
              editor.commands.insertContent('    ');
              return true;
            }
          }
          return true;
        }

        // 다른 모든 키는 기본 동작 허용
        return false;
      }
    }
  });

  // 초기 로드 시에만 에디터 내용 설정 (타이핑 중 업데이트 방지)
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (editor && !isInitialized) {
      if (content && content !== editor.getHTML()) {
        editor.commands.setContent(content);
      }
      setIsInitialized(true);
    }
  }, [editor, content, isInitialized]);

  if (!editor) {
    return null;
  }

  // 링크 추가/제거
  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const message = previousUrl
      ? 'URL을 수정하거나, 링크를 제거하려면 비워두세요:'
      : 'URL을 입력하세요:';
    const url = window.prompt(message, previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  // 이미지 추가
  const addImage = () => {
    const url = window.prompt('이미지 URL을 입력하세요:');

    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  // 텍스트 색상 변경
  const setTextColor = () => {
    const color = window.prompt('색상 코드를 입력하세요 (예: #FF0000):');
    if (color) {
      editor.chain().focus().setColor(color).run();
    }
  };

  // 하이라이트 색상 변경
  const setHighlightColor = () => {
    const color = window.prompt('하이라이트 색상을 입력하세요 (예: yellow, #FFFF00):');
    if (color) {
      editor.chain().focus().toggleHighlight({ color }).run();
    }
  };

  // 테이블 추가
  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  // 들여쓰기 기능
  const indent = () => {
    // 리스트 아이템인 경우 sink 사용
    if (editor.can().sinkListItem('listItem')) {
      editor.chain().focus().sinkListItem('listItem').run();
    } else {
      // 일반 텍스트인 경우 탭 문자 삽입
      editor.chain().focus().insertContent('    ').run();
    }
  };

  // 내어쓰기 기능
  const outdent = () => {
    // 리스트 아이템인 경우 lift 사용
    if (editor.can().liftListItem('listItem')) {
      editor.chain().focus().liftListItem('listItem').run();
    }
  };

  // 툴바 버튼 컴포넌트 (Material-UI)
  const EditorButton = ({ onClick, active, disabled, title, icon: Icon }) => (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          color={active ? 'primary' : 'default'}
          sx={{
            borderRadius: 1,
            '&:hover': {
              backgroundColor: active ? 'primary.light' : 'action.hover'
            }
          }}
        >
          <Icon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      backgroundColor: 'background.paper'
    }}>
      {/* 툴바 */}
      <Box sx={{
        display: 'flex',
        gap: 0.5,
        p: 1,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'grey.50',
        flexWrap: 'wrap'
      }}>
        {/* 텍스트 스타일 */}
        <EditorButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="굵게 (Ctrl+B)"
          icon={BoldIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="기울임 (Ctrl+I)"
          icon={ItalicIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="밑줄 (Ctrl+U)"
          icon={UnderlineIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive('code')}
          title="인라인 코드"
          icon={CodeIcon}
        />

        <Divider orientation="vertical" flexItem />

        {/* 색상 */}
        <EditorButton
          onClick={setTextColor}
          title="텍스트 색상"
          icon={PaletteIcon}
        />
        <EditorButton
          onClick={setHighlightColor}
          active={editor.isActive('highlight')}
          title="하이라이트"
          icon={HighlightIcon}
        />

        <Divider orientation="vertical" flexItem />

        {/* 제목 */}
        <EditorButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
          title="제목 1"
          icon={Heading1Icon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="제목 2"
          icon={Heading2Icon}
        />

        <Divider orientation="vertical" flexItem />

        {/* 정렬 */}
        <EditorButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="왼쪽 정렬"
          icon={AlignLeftIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="가운데 정렬"
          icon={AlignCenterIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          title="오른쪽 정렬"
          icon={AlignRightIcon}
        />

        <Divider orientation="vertical" flexItem />

        {/* 들여쓰기/내어쓰기 */}
        <EditorButton
          onClick={outdent}
          disabled={!editor.can().liftListItem('listItem')}
          title="내어쓰기 (Shift+Tab)"
          icon={OutdentIcon}
        />
        <EditorButton
          onClick={indent}
          title="들여쓰기 (Tab)"
          icon={IndentIcon}
        />

        <Divider orientation="vertical" flexItem />

        {/* 목록 */}
        <EditorButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="글머리 목록"
          icon={BulletListIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="번호 목록"
          icon={OrderedListIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          active={editor.isActive('taskList')}
          title="작업 목록"
          icon={TaskListIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="인용"
          icon={QuoteIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive('codeBlock')}
          title="코드 블록"
          icon={CodeBlockIcon}
        />

        <Divider orientation="vertical" flexItem />

        {/* 링크 & 이미지 */}
        <EditorButton
          onClick={setLink}
          active={editor.isActive('link')}
          title={editor.isActive('link') ? "링크 수정/제거 (비우면 제거)" : "링크 추가 (Ctrl+K)"}
          icon={LinkIcon}
        />
        <EditorButton
          onClick={addImage}
          title="이미지 삽입"
          icon={ImageIcon}
        />

        <Divider orientation="vertical" flexItem />

        {/* 테이블 */}
        <EditorButton
          onClick={insertTable}
          title="테이블 삽입"
          icon={TableIcon}
        />
        {editor.isActive('table') && (
          <>
            <EditorButton
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              title="열 추가"
              icon={ColumnIcon}
            />
            <EditorButton
              onClick={() => editor.chain().focus().addRowAfter().run()}
              title="행 추가"
              icon={RowIcon}
            />
            <EditorButton
              onClick={() => editor.chain().focus().deleteTable().run()}
              title="테이블 삭제"
              icon={DeleteIcon}
            />
          </>
        )}

        <Divider orientation="vertical" flexItem />

        {/* 실행 취소/다시 실행 */}
        <EditorButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="실행 취소 (Ctrl+Z)"
          icon={UndoIcon}
        />
        <EditorButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="다시 실행 (Ctrl+Y)"
          icon={RedoIcon}
        />
      </Box>

      {/* 에디터 */}
      <Box sx={{
        minHeight: 300,
        '& .ProseMirror': {
          minHeight: 300,
          padding: 2,
          outline: 'none',
          fontSize: '16px',
          lineHeight: 1.6,
          '& p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: 'text.disabled',
            pointerEvents: 'none',
            height: 0
          },
          '& h1': {
            fontSize: '2em',
            fontWeight: 600,
            marginTop: '0.67em',
            marginBottom: '0.67em'
          },
          '& h2': {
            fontSize: '1.5em',
            fontWeight: 600,
            marginTop: '0.83em',
            marginBottom: '0.83em'
          },
          '& ul, & ol': {
            paddingLeft: '1.5em'
          },
          '& code': {
            backgroundColor: 'grey.100',
            padding: '0.2em 0.4em',
            borderRadius: '3px',
            fontSize: '0.9em'
          },
          '& pre': {
            backgroundColor: 'grey.900',
            color: 'common.white',
            padding: '1em',
            borderRadius: '4px',
            overflow: 'auto'
          },
          '& blockquote': {
            borderLeft: '3px solid',
            borderColor: 'primary.main',
            paddingLeft: '1em',
            marginLeft: 0,
            fontStyle: 'italic'
          },
          '& table': {
            borderCollapse: 'collapse',
            margin: 0,
            overflow: 'hidden',
            tableLayout: 'fixed',
            width: '100%',
            '& td, & th': {
              border: '2px solid',
              borderColor: 'divider',
              padding: '8px',
              verticalAlign: 'top'
            },
            '& th': {
              backgroundColor: 'grey.100',
              fontWeight: 600
            }
          }
        }
      }}>
        <EditorContent editor={editor} />
      </Box>

      {/* 글자 수 표시 */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        p: 1,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'grey.50'
      }}>
        <Typography variant="caption" color="text.secondary">
          {editor.getText().length}자
        </Typography>
      </Box>
    </Box>
  );
}

export default RichTextEditor;